import { FC } from 'react';
import { getColorValue } from '@shinetools/sunshine';
import { Bar, ComposedChart, Line, XAxis, YAxis } from 'recharts';

import { BarLineGraphData } from 'features/Dashboard/types';

import {
  activeDot,
  GRAPH_ANIMATION_DURATION,
  GRAPH_ANIMATION_EASING,
  xAxisDateFormatter,
  yAxisLabelFormatter,
} from '../../../common';
import useIntervalContext from '../../hooks/useIntervalContext';
import CartesianGridNoDashHorizontal from '../CartesianGridNoDashHorizontal';
import Graph from '../Graph';
import GraphCursor from '../GraphCursor';
import GraphLegend from '../GraphLegend';
import BarWithBorder from './components/BarWithBorder';
import { MultiTooltip } from './components/MultiTooltip';
import locales from './locales';

interface BarLineGraphProps {
  data: BarLineGraphData[];
}

const BarLineGraph: FC<BarLineGraphProps> = ({ data }) => {
  const { granularity } = useIntervalContext();

  return (
    <Graph>
      <Graph.Header>
        <GraphLegend
          backgroundColor="white"
          borderColor="blue.800"
          borderRadius="radius-full"
          borderWidth={2}
          label={locales.balance}
        />
        <GraphLegend
          backgroundColor="blue.500"
          borderColor="blue.600"
          label={locales.earnings}
        />
        <GraphLegend
          backgroundColor="orange.300"
          borderColor="orange.500"
          label={locales.spendings}
        />
      </Graph.Header>
      <Graph.Body>
        <ComposedChart data={data}>
          {/*
              We need to pass the cursor here since the lib hacks
              the children to render stuff on the chart itself.
              If we pass it inside "MultiTooltip" `cursor` won't be
              a props of the Tooltip (it would be a "sub-prop")
              and thus will not be rendered...
              The allowedDataKeys prop allow the cursor to filter itself out if not needed
          */}
          <MultiTooltip
            cursor={<GraphCursor allowedDataKeys={['monthStartBalance']} />}
          />

          <CartesianGridNoDashHorizontal />

          <YAxis
            domain={['dataMin', 'dataMax']}
            dy={-6}
            orientation="right"
            padding={{ top: 10 }}
            stroke="white"
            strokeWidth={0.1}
            style={{ textAnchor: 'middle' }}
            tick={{ fill: getColorValue('grey.500'), fontSize: 10 }}
            tickFormatter={yAxisLabelFormatter}
            tickLine={false}
          />

          {/*TODO: it might be possible to use a 2nd xAxis to offset the legend*/}
          <XAxis
            dataKey="date"
            interval={1}
            stroke="white"
            tick={{ fill: getColorValue('grey.500'), fontSize: 10 }}
            tickFormatter={(label) => xAxisDateFormatter(label, granularity)}
            tickLine={false}
          />

          <Bar
            animationDuration={GRAPH_ANIMATION_DURATION}
            animationEasing={GRAPH_ANIMATION_EASING}
            barSize={16}
            dataKey="earnings"
            fill={getColorValue('blue.500')}
            shape={
              <BarWithBorder
                borderColor={getColorValue('blue.600')}
                dataKey="earnings"
              />
            }
          />

          <Bar
            animationDuration={GRAPH_ANIMATION_DURATION}
            animationEasing={GRAPH_ANIMATION_EASING}
            barSize={16}
            dataKey="spendings"
            fill={getColorValue('orange.300')}
            shape={
              <BarWithBorder
                borderColor={getColorValue('orange.500')}
                dataKey="spendings"
              />
            }
          />

          <Line
            activeDot={activeDot('blue.800')}
            animationDuration={GRAPH_ANIMATION_DURATION}
            animationEasing={GRAPH_ANIMATION_EASING}
            connectNulls
            dataKey="monthStartBalance"
            dot={{
              fill: 'white',
              r: 5,
              stroke: getColorValue('blue.800'),
              strokeWidth: 2,
            }}
            stroke={getColorValue('blue.800')}
            strokeLinecap="round"
            strokeWidth={2}
            type="linear"
          />
        </ComposedChart>
      </Graph.Body>
    </Graph>
  );
};

export default BarLineGraph;
